import { ClockIcon } from '@heroicons/react/24/outline'
import { Tooltip } from 'stories/components/Tooltip/Tooltip'
import type { Color } from 'stories/types'

interface InputProps {
  /**
   * What background color to use
   */
  color?: Color
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Is readOnly
   */
  readOnly?: boolean
  /**
   * Tooltip of Input
   */
  tooltip?: string
  /**
   * Is autofocus
   */
  autoFocus?: boolean
  /**
   * Type of Input
   */
  type?: string
  /**
   * Title of Input
   */
  title?: string
  /**
   * Placeholder of Input
   */
  placeholder?: string
  /**
   * Name of Input
   */
  name?: string
  /**
   * Value of Input
   */
  value?: { from?: string; to?: string }
  /**
   * Font Size of Input
   */
  fontSize?: number
  /**
   * Error of Input
   */
  error?: string
  /**
   * Custom class name
   */
  className?: string
  /**
   * Is has icon
   */
  hasIcon?: boolean
  /**
   * Is has icon
   */
  required?: boolean
  /**
   * Icon component
   */
  icon?: string | JSX.Element | null
  /**
   * Prefix
   */
  prefix?: string
  /**
   * onChange
   */
  history?: boolean

  additionalElements?: JSX.Element | Function | null

  onBlurNull?: boolean

  copyable?: boolean

  userSearch?: boolean

  transparent?: boolean

  onKeyDown?: (e: any) => void

  onChange: (e: any) => void // string | React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: () => void
  showHistory?: () => void
  onKeyPress?: (e: any) => void
}

/**
 * Primary UI component for user interaction
 */
export const InputDateRange = ({
  color = 'sky',
  disabled = false,
  readOnly = false,
  tooltip = '',
  autoFocus = false,
  title = '',
  name = '',
  value = {},
  fontSize = 15,
  error = '',
  className = '',
  hasIcon = false,
  icon = null,
  prefix = '',
  history = false,
  required = false,
  additionalElements = null,
  transparent = false,
  onChange = () => {},
  onBlur = () => {},
  showHistory = () => {},
  ...props
}: InputProps) => {
  const containerCls = [
    'flex',
    'items-justify',
    disabled ? 'bg-gray-100' : transparent ? 'bg-transparent' : 'bg-white',
    readOnly ? 'border-none font-variation-settings-600' : 'border',
    'border-gray-300',
  ]
  const classNames = [
    'block',
    'rounded-t',
    'px-2.5',
    'pb-[2px]',
    'pt-[27px]',
    'w-full',
    `text-[${fontSize}px]`,
    'text-gray-900',
    readOnly ? 'cursor-not-allowed' : '',
    'focus:outline-none',
    'focus:ring-0',
    'border-none',
    `focus:border-${color}-600`,
    'peer',
    prefix.length > 0 && 'pl-7',
    hasIcon && 'pl-9',
    error && 'border-rose-700',
    disabled && 'cursor-not-allowed',
  ]
  if (value === null) value = {}
  // value = InputConvert({ type }, value)

  // const inputRef = useRef<HTMLInputElement>(null)

  const changeInput = (type: string, e: any) => {
    let _value = e.target.value
    onChange({
      ...value,
      [type]: _value,
    })
  }

  return (
    <div className="input-container">
      <div className={`group relative z-0 w-full ${className}`}>
        {hasIcon && icon && typeof icon === 'string' && (
          <div className="flex absolute bottom-2 left-0 items-center pl-3 pointer-events-none">
            <img src={icon} className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </div>
        )}
        {hasIcon && icon && typeof icon !== 'string' && (
          <div className="flex absolute bottom-2 left-0 items-center pl-3 pointer-events-none">{icon}</div>
        )}

        {prefix.length > 0 && (
          <div className="text-gray-600 text-[14px] flex absolute top-[29px] left-0 items-center pl-3 pointer-events-none">
            {prefix}
          </div>
        )}
        <div className={containerCls.join(' ')}>
          <input
            type="date"
            name={`${name}-from`}
            id={`${name}-from`}
            className={classNames.join(' ')}
            placeholder="From"
            disabled={disabled}
            autoFocus={autoFocus}
            value={value.from || ''}
            required={required}
            onChange={(e) => changeInput('from', e)}
            onBlur={onBlur}
            readOnly={readOnly}
            {...props}
          />
          <p className="pt-[27px]">-</p>
          <input
            type="date"
            name={`${name}-to`}
            id={`${name}-to`}
            className={classNames.join(' ')}
            placeholder="To"
            disabled={disabled}
            value={value.to || ''}
            required={required}
            onChange={(e) => changeInput('to', e)}
            onBlur={onBlur}
            readOnly={readOnly}
            {...props}
          />
        </div>
        <label
          htmlFor={name}
          className="absolute text-[12px] text-gray-700 top-1.5 border-b z-10 origin-[0] left-2.5 flex gap-2 items-center"
        >
          {title} (From - To)
          {required && '*'}
          {tooltip.length > 0 ? <Tooltip message={tooltip}></Tooltip> : null}
          {additionalElements !== null &&
            (typeof additionalElements == 'function' ? additionalElements(onChange) : additionalElements)}
          {history && (
            <span className="ml-1 hidden group-hover:inline" onClick={() => showHistory()}>
              <ClockIcon className="h-[14px] w-[14px] text-gray-600 cursor-pointer" aria-hidden="true" />
            </span>
          )}
        </label>
      </div>
      {error && (
        <p className={`${readOnly && 'ml-2'} peer-invalid:visible text-rose-700 text-[13px] pt-[1px] pl-1`}>{error}</p>
      )}
    </div>
  )
}
